import type { LeadFollowup } from './leadFollowups';
import { isRunningOut, progressOf } from './leadLoop';
import type { LeadLoop } from './leadLoop';
import type { PipelineRow, PipelineSummary } from './leadPipeline';
import type { RunningRound } from './leadRounds';

/**
 * Drop C: the rules that mark a lead as needing a look.
 *
 * ── The question ───────────────────────────────────────────────────────────
 *
 * Alex's, in his words: leads falling through the cracks. Drop B answered
 * "what is the team doing"; this answers "what is the team NOT doing that it
 * should be". Three rules, each a thing that is already true in the data and
 * was never added up.
 *
 * ── No new thresholds ──────────────────────────────────────────────────────
 *
 * Every number here is one the Hub already uses somewhere else, so this page
 * cannot disagree with the Leads tab about what "quiet" means:
 *
 *   14 days      the "No touch in 14 days" chip in the Leads filter bar
 *   behind pace  isRunningOut, the rounds arithmetic since December
 *   window_closed  lead_rounds.ended_reason, the reason the sweep writes
 *
 * ── What "handled" means, and why it excludes a lead from every rule ───────
 *
 * A lead in a running round, or with an open follow-up task, is being handled:
 * somebody has committed to what happens next. That is the exact condition
 * behind the Leads tab's "no next action" filter, and it is the difference
 * between a lead that is quiet and a lead that is forgotten. So a lead that is
 * handled is never flagged here, however long it has been. The rule is about
 * the crack, not about the calendar.
 *
 * ── What is deliberately not here ──────────────────────────────────────────
 *
 * "A lane whose never-emailed count is RISING" was in the design note. Rising
 * needs a yesterday to compare with, and the Hub keeps no snapshot of this
 * page. Building one is a migration, and the note's own rule is that this
 * rework needs none. So the lane read is the one the page already has: pick a
 * lane in the filter bar and these chips narrow to it.
 */

export type AttentionKey = 'behind' | 'round_closed' | 'quiet';

export const ATTENTION_ORDER: readonly AttentionKey[] = ['behind', 'round_closed', 'quiet'];

export const ATTENTION_LABELS: Record<AttentionKey, string> = {
  behind: 'behind pace',
  round_closed: 'round closed, no answer',
  quiet: 'gone quiet',
};

export const ATTENTION_RULE_EXPLAINERS: Record<AttentionKey, string> = {
  behind:
    'A running round with more follow-ups left than days to send them in. Not missed yet — the window is still open — but it cannot be finished at one a day without acting today.',
  round_closed:
    'The last round ended because its window closed with no reply, and nobody has started the next one or set a follow-up. The lead is not in anything.',
  quiet:
    'A lead still moving whose last touch was more than 14 days ago, with no running round and no open follow-up. Same 14 days as the Leads tab. A lead with a next step set is not here however long it has been quiet — that one is handled.',
};

/** The 14 the Leads tab already uses. One number, one meaning. */
export const QUIET_AFTER_DAYS = 14;

export interface AttentionLine {
  key: AttentionKey;
  label: string;
  count: number;
  leadIds: string[];
}

/** Positions that mean the lead is still being worked. */
const MOVING = new Set(['new', 'contacted', 'engaged', 'converted']);

function daysBetween(from: string, to: string): number {
  const ms = Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`);
  return Math.max(0, Math.round(ms / 86400000));
}

export function buildAttention(input: {
  summary: PipelineSummary;
  running: ReadonlyMap<string, RunningRound>;
  loops: ReadonlyMap<string, LeadLoop>;
  followups: readonly LeadFollowup[];
  today: string;
}): AttentionLine[] {
  const { summary, running, loops, followups, today } = input;

  const moving: PipelineRow[] = summary.bands
    .filter((band) => MOVING.has(band.key))
    .flatMap((band) => band.rows);

  // Handled: a next step exists. Open tasks with a due date, on any lead.
  const withTask = new Set(
    followups
      .filter((task) => !task.is_completed && task.due_date !== null)
      .map((task) => task.lead_id),
  );
  const handled = (leadId: string) => running.has(leadId) || withTask.has(leadId);

  const behind: string[] = [];
  const roundClosed: string[] = [];
  const quiet: string[] = [];

  for (const row of moving) {
    const id = row.leadId;

    // Behind pace is the one rule that applies TO a running round rather than
    // to a lead without one.
    const round = running.get(id);
    if (round !== undefined && isRunningOut(progressOf(round, today))) {
      behind.push(id);
      continue;
    }
    if (handled(id)) continue;

    if (loops.get(id)?.lastEndedReason === 'window_closed') {
      roundClosed.push(id);
      continue;
    }

    if (row.lastTouchOn !== null && daysBetween(row.lastTouchOn, today) > QUIET_AFTER_DAYS) {
      quiet.push(id);
    }
  }

  const line = (key: AttentionKey, leadIds: string[]): AttentionLine => ({
    key,
    label: ATTENTION_LABELS[key],
    count: leadIds.length,
    leadIds,
  });
  return [line('behind', behind), line('round_closed', roundClosed), line('quiet', quiet)];
}
